import React, { useContext, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRightOutlined, InboxOutlined } from '@ant-design/icons';
import {
  Button,
  message,
  Result,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import { DataChangeRequestValue } from '../components/dataChangeRequests/DataChangeRequestStatus';
import NetworkTag from '../components/networks/NetworkTag';
import { MAX_ITEM_AGGREGATE } from '../constants';
import { UserContext } from '../contexts/UserContext';
import {
  ListAdminDataChangeRequestsQuery,
  Networks_Bool_Exp,
  Practices_Bool_Exp,
  useApplyNetworkDataChangeRequestMutation,
  useApplyPracticeDataChangeRequestMutation,
  useClaimAdminDataChangeRequestMutation,
  useFinalizeAdminDataChangeRequestMutation,
  useListAdminDataChangeRequestsQuery,
  useReleaseAdminDataChangeRequestMutation,
  useRejectAdminDataChangeRequestMutation,
} from '../graphql/generated';
import { requestToNetworkMergeVarChanges } from './clientNetworkChanges';
import { requestToPracticeProposedChanges } from './clientPracticeChanges';

type ChangeRequest = ListAdminDataChangeRequestsQuery['pending_requests'][0];
type Network = ListAdminDataChangeRequestsQuery['networks'][0];

const PAGE_SIZE = 20;

const requestChanges = (record: ChangeRequest) =>
  record.target_table === 'networks'
    ? requestToNetworkMergeVarChanges(record)
    : requestToPracticeProposedChanges(record);

const hasOwn = (value: object, field: PropertyKey) =>
  Object.prototype.hasOwnProperty.call(value, field);

const practiceOriginalValues = (record: ChangeRequest) => {
  const originals = (record.original_values || {}) as Record<string, unknown>;
  return Object.fromEntries(
    Object.keys(requestToPracticeProposedChanges(record))
      .filter((field) => hasOwn(originals, field))
      .map((field) => [field, originals[field]])
  );
};

const networkOriginalValues = (record: ChangeRequest) => {
  const originals = (record.original_values || {}) as {
    network_merge_vars?: Record<string, unknown>;
  };
  const mergeVars = originals.network_merge_vars || {};
  return Object.fromEntries(
    Object.keys(requestToNetworkMergeVarChanges(record))
      .filter((field) => hasOwn(mergeVars, field))
      .map((field) => [field, mergeVars[field]])
  );
};

const hasOriginalValueForEveryChange = (record: ChangeRequest) => {
  const changes = requestChanges(record);
  const originals =
    record.target_table === 'networks'
      ? networkOriginalValues(record)
      : practiceOriginalValues(record);
  return Object.keys(changes).every((field) => hasOwn(originals, field));
};

const isApprovable = (record: ChangeRequest) =>
  record.change_type === 'update' &&
  ((record.target_table === 'practices' &&
    Boolean(record.target_practice_id)) ||
    (record.target_table === 'networks' &&
      Boolean(record.target_network_id))) &&
  Object.keys(requestChanges(record)).length > 0 &&
  hasOriginalValueForEveryChange(record);

const practiceConsistencyWhere = (
  record: ChangeRequest,
  claimedUpdatedAt: string
): Practices_Bool_Exp => {
  const comparisons = Object.entries(practiceOriginalValues(record)).map(
    ([field, value]) => ({
      [field]: value === null ? { _is_null: true } : { _eq: value },
    })
  ) as Practices_Bool_Exp[];

  return {
    _and: [
      { practice_id: { _eq: record.target_practice_id } },
      {
        change_requests: {
          data_change_request_id: { _eq: record.data_change_request_id },
          status: { _eq: 'reviewing' },
          updated_at: { _eq: claimedUpdatedAt },
        },
      },
      ...comparisons,
    ],
  };
};

const networkConsistencyWhere = (
  record: ChangeRequest,
  claimedUpdatedAt: string
): Networks_Bool_Exp => {
  const comparisons = Object.entries(networkOriginalValues(record)).map(
    ([field, value]) => {
      if (value === '' || value === null) {
        return {
          _or: [
            { network_merge_vars: { _is_null: true } },
            { network_merge_vars: { _contains: { [field]: value } } },
            { network_merge_vars: { _contains: { [field]: null } } },
            { _not: { network_merge_vars: { _has_key: field } } },
          ],
        };
      }
      return { network_merge_vars: { _contains: { [field]: value } } };
    }
  ) as Networks_Bool_Exp[];

  return {
    _and: [
      { network_id: { _eq: record.target_network_id } },
      {
        change_requests: {
          data_change_request_id: { _eq: record.data_change_request_id },
          status: { _eq: 'reviewing' },
          updated_at: { _eq: claimedUpdatedAt },
        },
      },
      ...comparisons,
    ],
  };
};

const PRACTICE_FIELD_LABELS: Record<string, string> = {
  practice_name: 'Name',
  practice_address: 'Address',
  practice_language: 'Language',
  practice_logo: 'Logo',
  primary_network_id: 'Network',
  is_archived: 'Active',
};

const NETWORK_FIELD_LABELS: Record<string, string> = {
  dce_name: 'ACO Name',
  dce_address1: 'Address line 1',
  dce_address2: 'Address line 2',
  dce_address3: 'Address line 3',
  dce_provider_contact: 'Providers Contact',
  dce_phone: 'Phone',
  dce_url: 'Website',
  dce_logo: 'Logo',
  dce_color: 'Color',
};

const STATUS_COLORS: Record<string, string> = {
  submitted: 'gold',
  reviewing: 'blue',
  approved: 'green',
  rejected: 'red',
  cancelled: 'default',
};

const ChangeValue: React.FC<{
  field: string;
  networks: Network[];
  previous?: boolean;
  value: unknown;
}> = ({ field, networks, previous, value }) => {
  if (field === 'practice_logo' || field === 'dce_logo') {
    return value ? (
      <img
        alt={previous ? 'current logo' : 'proposed logo'}
        src={String(value)}
        style={{ maxHeight: 24, maxWidth: 64, verticalAlign: 'middle' }}
      />
    ) : (
      <DataChangeRequestValue previous={previous} />
    );
  }

  if (field === 'primary_network_id') {
    const network = networks.find(({ network_id }) => network_id === value);
    return network ? (
      <NetworkTag network={network} />
    ) : (
      <DataChangeRequestValue previous={previous} value={value as string} />
    );
  }

  const displayValue =
    field === 'is_archived' && typeof value === 'boolean'
      ? value
        ? 'No'
        : 'Yes'
      : (value as React.ReactNode);
  return <DataChangeRequestValue previous={previous} value={displayValue} />;
};

const ProposedChanges: React.FC<{
  record: ChangeRequest;
  networks: Network[];
}> = ({ record, networks }) => {
  const isNetwork = record.target_table === 'networks';
  const changes = requestChanges(record);
  const originalValues = isNetwork
    ? ((record.original_values || {}) as {
        network_merge_vars?: Record<string, unknown>;
      }).network_merge_vars || {}
    : ((record.original_values || {}) as Record<string, unknown>);
  const currentValues = isNetwork
    ? ((record.network?.network_merge_vars || {}) as Record<string, unknown>)
    : (record.practice as Record<string, unknown> | null);
  const labels = isNetwork ? NETWORK_FIELD_LABELS : PRACTICE_FIELD_LABELS;
  const isPendingReview = ['submitted', 'reviewing'].includes(record.status);

  return (
    <Space direction="vertical" size={4}>
      {Object.entries(changes).map(([field, proposed]) => {
        const original = originalValues[field];
        const current = currentValues?.[field];
        const currentDiffers =
          isPendingReview &&
          (isNetwork ? original ?? '' : original) !==
            (isNetwork ? current ?? '' : current);
        return (
          <div key={field}>
            <div>
              <Typography.Text type="secondary">
                {labels[field] || field}:{' '}
              </Typography.Text>
              <ChangeValue
                field={field}
                networks={networks}
                previous
                value={original}
              />{' '}
              <ArrowRightOutlined style={{ fontSize: 10 }} />{' '}
              <ChangeValue field={field} networks={networks} value={proposed} />
            </div>
            {currentDiffers ? (
              <div style={{ marginLeft: 12 }}>
                <Typography.Text type="warning">Current: </Typography.Text>
                <ChangeValue
                  field={field}
                  networks={networks}
                  value={current}
                />
              </div>
            ) : null}
          </div>
        );
      })}
      {record.submission_note ? (
        <Typography.Text type="secondary" style={{ fontStyle: 'italic' }}>
          “{record.submission_note}”
        </Typography.Text>
      ) : null}
    </Space>
  );
};

const DataChangeRequestsPage: React.FC = () => {
  const { user } = useContext(UserContext);
  const [pendingPage, setPendingPage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);
  const { data, loading, error, refetch } = useListAdminDataChangeRequestsQuery(
    {
      variables: {
        count_aggregate_limit: MAX_ITEM_AGGREGATE,
        pending_limit: PAGE_SIZE,
        pending_offset: (pendingPage - 1) * PAGE_SIZE,
        resolved_limit: PAGE_SIZE,
        resolved_offset: (resolvedPage - 1) * PAGE_SIZE,
      },
      notifyOnNetworkStatusChange: true,
    }
  );
  const networks = useMemo(() => data?.networks || [], [data]);

  const [claimRequest, claimResult] = useClaimAdminDataChangeRequestMutation();
  const [
    applyPractice,
    applyPracticeResult,
  ] = useApplyPracticeDataChangeRequestMutation();
  const [
    applyNetwork,
    applyNetworkResult,
  ] = useApplyNetworkDataChangeRequestMutation();
  const [
    finalizeRequest,
    finalizeResult,
  ] = useFinalizeAdminDataChangeRequestMutation();
  const [
    releaseRequest,
    releaseResult,
  ] = useReleaseAdminDataChangeRequestMutation();
  const [
    rejectRequest,
    rejectResult,
  ] = useRejectAdminDataChangeRequestMutation();

  const reviewStamp = (status: 'approved' | 'rejected') => ({
    status,
    reviewed_by_user_id: user?.user_id,
    reviewed_at: new Date().toISOString(),
  });

  const releaseClaim = async (
    record: ChangeRequest,
    claimedUpdatedAt: string
  ) => {
    const result = await releaseRequest({
      variables: {
        data_change_request_id: record.data_change_request_id,
        expected_updated_at: claimedUpdatedAt,
      },
    });
    return result.data?.update_data_change_requests?.affected_rows === 1;
  };

  const approve = async (record: ChangeRequest) => {
    if (!isApprovable(record)) {
      message.error(
        'This request is missing supported changes or their original values.'
      );
      return;
    }

    try {
      const claim = await claimRequest({
        variables: {
          data_change_request_id: record.data_change_request_id,
          expected_updated_at: record.updated_at,
        },
      });
      const claimed = claim.data?.update_data_change_requests;
      const claimedUpdatedAt = claimed?.returning[0]?.updated_at;
      if (claimed?.affected_rows !== 1 || !claimedUpdatedAt) {
        message.error(
          'This request changed after it was loaded. Refresh and review the latest version.'
        );
        await refetch();
        return;
      }

      let appliedRows = 0;
      if (record.target_table === 'practices' && record.target_practice_id) {
        const applied = await applyPractice({
          variables: {
            where: practiceConsistencyWhere(record, claimedUpdatedAt),
            practice_changes: requestToPracticeProposedChanges(record),
          },
        });
        appliedRows = applied.data?.update_practices?.affected_rows || 0;
      } else if (
        record.target_table === 'networks' &&
        record.target_network_id
      ) {
        const applied = await applyNetwork({
          variables: {
            where: networkConsistencyWhere(record, claimedUpdatedAt),
            merge_var_changes: requestToNetworkMergeVarChanges(record),
          },
        });
        appliedRows = applied.data?.update_networks?.affected_rows || 0;
      }

      if (appliedRows !== 1) {
        const released = await releaseClaim(record, claimedUpdatedAt);
        message.error(
          released
            ? 'The target changed after this request was submitted. The request was not applied.'
            : 'The target changed, and the request could not be returned to pending. Refresh before retrying.'
        );
        await refetch();
        return;
      }

      const finalized = await finalizeRequest({
        variables: {
          data_change_request_id: record.data_change_request_id,
          expected_updated_at: claimedUpdatedAt,
          review: reviewStamp('approved'),
        },
      });
      if (finalized.data?.update_data_change_requests?.affected_rows !== 1) {
        message.error(
          'The target was updated, but the request could not be marked approved. Refresh before taking further action.'
        );
        await refetch();
        return;
      }

      message.success('Request approved.');
      await refetch();
    } catch (mutationError) {
      message.error(
        mutationError instanceof Error
          ? mutationError.message
          : 'The request could not be approved.'
      );
      await refetch();
    }
  };

  const reject = async (record: ChangeRequest) => {
    try {
      const result = await rejectRequest({
        variables: {
          data_change_request_id: record.data_change_request_id,
          expected_updated_at: record.updated_at,
          review: reviewStamp('rejected'),
        },
      });
      if (result.data?.update_data_change_requests?.affected_rows === 1)
        message.success('Request rejected.');
      else
        message.error(
          'This request changed after it was loaded. Refresh and review the latest version.'
        );
      await refetch();
    } catch (mutationError) {
      message.error(
        mutationError instanceof Error
          ? mutationError.message
          : 'The request could not be rejected.'
      );
      await refetch();
    }
  };

  const reviewing =
    claimResult.loading ||
    applyPracticeResult.loading ||
    applyNetworkResult.loading ||
    finalizeResult.loading ||
    releaseResult.loading ||
    rejectResult.loading;

  const identityColumns: ColumnsType<ChangeRequest> = [
    {
      title: 'Target',
      key: 'target',
      render: (_, record) =>
        record.network ? (
          <NetworkTag network={record.network} />
        ) : record.practice ? (
          <Link to={`/practices?query=${record.practice.practice_id}`}>
            {record.practice.practice_name}
          </Link>
        ) : (
          <Typography.Text type="secondary">Unavailable target</Typography.Text>
        ),
    },
    {
      title: 'Submitted By',
      key: 'submitted_by',
      render: (_, record) => (
        <>
          {record.submitted_by_user.user_name}
          <br />
          <Typography.Text type="secondary">
            {record.submitted_by_user.user_email}
          </Typography.Text>
        </>
      ),
    },
    {
      title: 'Submitted',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (createdAt) => dayjs(createdAt).format('lll'),
    },
  ];

  const pendingColumns: ColumnsType<ChangeRequest> = [
    ...identityColumns,
    {
      title: 'Proposed Changes',
      key: 'changes',
      render: (_, record) => (
        <ProposedChanges record={record} networks={networks} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) =>
        record.status === 'reviewing' ? (
          <Tag color="blue">Reviewing</Tag>
        ) : (
          <Space>
            <Button
              disabled={!isApprovable(record)}
              loading={reviewing}
              onClick={() => approve(record)}
              size="small"
              type="primary"
            >
              Approve
            </Button>
            <Button
              danger
              loading={reviewing}
              onClick={() => reject(record)}
              size="small"
            >
              Reject
            </Button>
          </Space>
        ),
    },
  ];

  const resolvedColumns: ColumnsType<ChangeRequest> = [
    ...identityColumns,
    {
      title: 'Changes',
      key: 'changes',
      render: (_, record) => (
        <ProposedChanges record={record} networks={networks} />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Reviewed By',
      key: 'reviewed_by',
      render: (_, record) => record.reviewed_by_user?.user_name,
    },
    {
      title: 'Reviewed',
      dataIndex: 'reviewed_at',
      key: 'reviewed_at',
      render: (reviewedAt) =>
        reviewedAt ? dayjs(reviewedAt).format('lll') : null,
    },
  ];

  if (loading && !data) return <Skeleton />;
  if (error)
    return (
      <Result
        status="error"
        title="There was an error retrieving change requests"
        subTitle={error.message}
      />
    );

  return (
    <>
      <Helmet>
        <title>Change Requests | Mabel</title>
      </Helmet>
      <Typography.Title level={3}>
        <InboxOutlined /> Change Requests
      </Typography.Title>
      <Typography.Title level={5}>Pending Review</Typography.Title>
      <Table<ChangeRequest>
        columns={pendingColumns}
        dataSource={data?.pending_requests || []}
        loading={loading}
        pagination={{
          current: pendingPage,
          onChange: setPendingPage,
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total}${
              total === MAX_ITEM_AGGREGATE ? '+' : ''
            } items`,
          total: data?.pending_requests_aggregate.aggregate?.count || 0,
        }}
        rowKey="data_change_request_id"
        size="small"
      />
      <Typography.Title level={5} style={{ marginTop: 32 }}>
        Resolved
      </Typography.Title>
      <Table<ChangeRequest>
        columns={resolvedColumns}
        dataSource={data?.resolved_requests || []}
        loading={loading}
        pagination={{
          current: resolvedPage,
          onChange: setResolvedPage,
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total}${
              total === MAX_ITEM_AGGREGATE ? '+' : ''
            } items`,
          total: data?.resolved_requests_aggregate.aggregate?.count || 0,
        }}
        rowKey="data_change_request_id"
        size="small"
      />
    </>
  );
};

export default DataChangeRequestsPage;
